import { formatQueryParams } from './formatQueryParams';

type EmployeeProps = {
  employerId: string;
  employeeId: string;
  impersonatedEmployeeId?: string;
};
type OnboardingPeriodProps = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  impersonatedEmployeeId?: string;
};

type EmployeePlansProps = OnboardingPeriodProps & {
  benefitId?: string;
};

type CarrierApplicationProps = OnboardingPeriodProps;

/** Enrollment Copilot launch handoff — keyed by benefit + employee, exactly like
    the Copilot BFF route (one fill session = one benefit at one carrier). */
type EnrollmentCopilotLaunchProps = {
  benefitId: string;
  employeeId: string;
};

export const COMMON_ROUTES = {
  LOGIN: '/account/login',
  UNAUTHORIZED: '/unauthorized',
} as const;

export const COMMON_DYNAMIC_ROUTES = {
  HOME: () => '/',
  LOGIN: () => '/account/login',
  UNAUTHORIZED: () => '/unauthorized',
} as const;

const TORNADO_V2_ROUTES_PREFIX = '/v2';

export const TORNADO_V2_ROUTES = {
  PLAN_SEARCH_ANONYMOUS: () => `${TORNADO_V2_ROUTES_PREFIX}/plan-search`,
  PLAN_SEARCH_RESULTS_ANONYMOUS: () =>
    `${TORNADO_V2_ROUTES_PREFIX}/plan-search/results`,
  WELCOME: (quoteId: string, quotePlanDesignId?: string) =>
    quotePlanDesignId
      ? `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}/welcome`
      : `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/welcome`,
  CONTRIBUTION: (quoteId: string, quotePlanDesignId: string) =>
    `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}/contribution`,
  PLAN_MATCHING_STRATEGY: (quoteId: string, quotePlanDesignId: string) =>
    `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}/plan-matching-strategy`,
  PRIORITIES: (quoteId: string, quotePlanDesignId: string) =>
    `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}/priorities`,
  SAVINGS: (quoteId: string, quotePlanDesignId: string) =>
    `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}/savings`,
  PLAN_SEARCH: (quoteId: string, quotePlanDesignId?: string) => {
    const prefixPath =
      quoteId && quotePlanDesignId
        ? `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}`
        : `${TORNADO_V2_ROUTES_PREFIX}`;
    return `${prefixPath}/plan-search`;
  },
  PLAN_SEARCH_RESULTS: (quoteId: string, quotePlanDesignId?: string) => {
    const route =
      quoteId && quotePlanDesignId
        ? `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}/results`
        : `${TORNADO_V2_ROUTES_PREFIX}/plan-search/results`;
    return route;
  },
  PLAN_DESIGN_SUMMARY: (quoteId: string, quotePlanDesignId: string) =>
    `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}/plan-design-summary`,
  EMPLOYEE_BREAKDOWN_V2: (quoteId: string, quotePlanDesignId: string) =>
    `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}/employee-breakdown`,
  ALLOWANCE_V2: (quoteId: string, quotePlanDesignId: string) =>
    `${TORNADO_V2_ROUTES_PREFIX}/${quoteId}/${quotePlanDesignId}/allowance`,
} as const;

export const APP_ROUTES = {
  // *** Common
  UNAUTHORIZED: () => '/unauthorized',
  LOGIN: () => '/account/login',
  ERROR: () => '/500',
  NOT_FOUND: () => '/404',
  CARRIER_PAYMENT: (benefitId: string) =>
    formatQueryParams('/carrier-payment', { benefitId }),
  // **********

  // *** Employee
  UNSUPPORTED_STATE: () => '/employee/unsupported-state',
  PERSONALIZED_ALLOWANCE: () => '/employee/personalized-allowance',
  REPORT: () => '/employee/report',
  SELF_REPORT: () => '/employee/self-report',
  THANK_YOU: () => '/employee/thank-you',
  NO_COVERAGE_NEEDED: () => '/employee/no-coverage-needed',
  HEALTHCARE_INSURED: () => '/employee/healthcare-insured',
  HEALTHCARE_INSURED_REDIRECT: () => '/employee/healthcare-insured-redirect',
  HEALTHCARE_PREFERENCES: () => '/employee/healthcare-preferences',
  PREFERENCES: () => '/employee/preferences',
  COVERAGE_OPTIONS: () => '/employee/coverage-options',
  PLANS: () => '/employee/plans',
  MEDICARE_AND_REGULAR_PLAN_ELECTION: () =>
    '/employee/medicare-and-regular-plan-election',
  MEDICARE_PLAN_ELECTION: () => '/employee/medicare-plan-election',
  HEALTHCARE_NEEDS: () => '/employee/healthcare-needs',
  OPT_OUT: () => '/employee/opt-out',
  REVIEW_AND_CONFIRM: () => '/employee/review-and-confirm',
  QLE: () => '/employee/qle',
  HOME: () => '/employee/home',
  PERSONAL_DETAILS: () => '/employee/personal-details',
  PARTNERS_HUB: () => '/employee/partners-hub',
  ENROLLMENT_DETAILS: ({
    onboardingPeriodId,
  }: {
    onboardingPeriodId: string;
  }) => `/employee/enrollments/${onboardingPeriodId}/enrollment-details`,
  FILES: ({ onboardingPeriodId }: { onboardingPeriodId: string }) =>
    `/employee/enrollments/${onboardingPeriodId}/files`,
  PAYMENTS: ({ onboardingPeriodId }: { onboardingPeriodId: string }) =>
    `/employee/enrollments/${onboardingPeriodId}/payments`,
  DISCOVERY_SHOPPING_PREFERENCES: () =>
    '/employee/discovery-shopping/preferences',
  DISCOVERY_SHOPPING_RECOMMENDATIONS: () =>
    '/employee/discovery-shopping/recommendations',
  DISCOVERY_SHOPPING_CHAT: () => '/employee/discovery-shopping/chat',
  DISCOVERY_SHOPPING_PLANS: () => '/employee/discovery-shopping/plans',
  DISCOVERY_SHOPPING_WAIVE_CONFIRMATION: () =>
    '/employee/discovery-shopping/waive/confirmation',
  DISCOVERY_SHOPPING_PROFILING: () => '/employee/discovery-shopping/profiling',
  DISCOVERY_SHOPPING_MEDICARE: () => '/employee/discovery-shopping/medicare',
  DISCOVERY_SHOPPING_MEDICARE_AND_REGULAR: () =>
    '/employee/discovery-shopping/medicare-and-regular',
  DISCOVERY_SHOPPING_REVIEW_AND_CONFIRM: () =>
    '/employee/discovery-shopping/review-and-confirm',
  DISCOVERY_SHOPPING_REVIEW_AND_CONFIRM_V2: () =>
    '/employee/discovery-shopping/review-and-confirm-v2',
  DISCOVERY_SHOPPING_THANK_YOU: () => '/employee/discovery-shopping/thank-you',
  CARD: () => '/card',
  // **********

  // *** Don
  DON_COMPANY: (impersonatedEmployeeId?: string) =>
    formatQueryParams('/admin/company', { impersonatedEmployeeId }),
  DON_EMPLOYEES: (props: {
    impersonatedEmployeeId?: string;
    openWith?: string;
  }) =>
    formatQueryParams('/admin/employees', {
      impersonatedEmployeeId: props.impersonatedEmployeeId,
      openWith: props.openWith,
    }),
  DON_DOCUMENTS: (openWith?: string) =>
    formatQueryParams('/admin/documents', { openWith }),
  DON_COMPLIANCE: () => '/admin/compliance',
  DON_PAYROLL: (props: { openWith?: string }) =>
    formatQueryParams('/admin/payroll', { openWith: props.openWith }),
  DON_ZORRO_PAY: () => '/admin/zorro-pay',
  DON_EMPLOYEE_PAGE: ({
    employerId,
    employeeId,
    impersonatedEmployeeId,
  }: EmployeeProps) =>
    formatQueryParams(`/admin/${employerId}/employees/${employeeId}`, {
      impersonatedEmployeeId,
    }),
  DON_EMPLOYEE_PERSONAL_DETAILS: ({ employerId, employeeId }: EmployeeProps) =>
    `/admin/${employerId}/employees/${employeeId}/personal-details`,
  DON_EMPLOYEE_GENERAL_NOTES: ({ employerId, employeeId }: EmployeeProps) =>
    `/admin/${employerId}/employees/${employeeId}/general-notes`,
  DON_EMPLOYEE_NO_ENROLLMENTS: ({ employerId, employeeId }: EmployeeProps) =>
    `/admin/${employerId}/employees/${employeeId}/no-enrollments`,
  DON_EMPLOYEE_PAYMENT_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
  }: OnboardingPeriodProps) =>
    `/admin/${employerId}/employees/${employeeId}/enrollments/${onboardingPeriodId}/payments`,
  USER_ACTIVATION: (props: { activationId: string }) =>
    `/admin/user-activation/${props.activationId}`,
  DON_ADD_EMPLOYER_GET_STARTED: (
    employerId: string,
    employerName: string,
    activationLink: string
  ) =>
    `/admin/add-employer/${employerId}/${encodeURIComponent(employerName)}/get-started?activationLink=${activationLink}`,

  DON_ADD_EMPLOYER_COMPANY_DETAILS: (employerId?: string) =>
    employerId
      ? `/admin/add-employer/${employerId}/company-details`
      : '/admin/add-employer',
  DON_ADD_EMPLOYER_CONTACTS: (employerId: string) =>
    `/admin/add-employer/${employerId}/contacts`,
  DON_ADD_EMPLOYER_COMPANY_SETUP: (employerId: string) =>
    `/admin/add-employer/${employerId}/company-setup`,

  DON_ADD_EMPLOYER_REVIEW: (employerId: string) =>
    `/admin/add-employer/${employerId}/review`,
  DON_ADD_EMPLOYER_THANK_YOU: (employerId: string) =>
    `/admin/add-employer/${employerId}/thank-you`,

  CANCEL_INVITATION: (employerId: string) =>
    `/admin/${employerId}/cancel-invitation`,

  DON_COMPANY_SETUP_COMPANY_DETAILS: (employerId: string) =>
    `/admin/company-setup/${employerId}/company-details`,
  DON_COMPANY_SETUP_COMPANY_SETUP: (employerId: string) =>
    `/admin/company-setup/${employerId}/company-setup`,
  DON_COMPANY_SETUP_CONTACTS: (employerId: string) =>
    `/admin/company-setup/${employerId}/contacts`,
  DON_COMPANY_SETUP_PLAN_YEAR_SETUP: (employerId: string) =>
    `/admin/company-setup/${employerId}/plan-year-setup`,
  DON_COMPANY_SETUP_PAYROLL_SYNC: (employerId: string) =>
    `/admin/company-setup/${employerId}/payroll-sync`,

  DON_EMPLOYEE_PLANS_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
  }: EmployeePlansProps) =>
    formatQueryParams(
      `/admin/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/plans`,
      { impersonatedEmployeeId }
    ),
  DON_EMPLOYEE_ACTIVITY_LOG_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
  }: OnboardingPeriodProps) =>
    formatQueryParams(
      `/admin/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/activity-log`,
      { impersonatedEmployeeId }
    ),
  DON_EMPLOYEE_FILES_TAB_V2: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
  }: OnboardingPeriodProps) =>
    formatQueryParams(
      `/admin/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/files`,
      { impersonatedEmployeeId }
    ),
  DON_EMPLOYEE_ZORRO_PAY_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
  }: OnboardingPeriodProps) =>
    formatQueryParams(
      `/admin/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/zorro-pay`,
      { impersonatedEmployeeId }
    ),
  DON_EMPLOYEE_MEMBERS_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
  }: OnboardingPeriodProps) =>
    formatQueryParams(
      `/admin/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/members`,
      { impersonatedEmployeeId }
    ),
  // **********

  // *** Ops
  OPS_DASHBOARD: () => '/ops/dashboard',
  OPS_DASHBOARD_WORKFLOW: (workflowId: string) =>
    `/ops/dashboard/workflows/${workflowId}`,
  OPS_EMPLOYERS: () => '/ops/employers',
  OPS_DEMO_EMPLOYERS: () => '/ops/demo-employers',
  OPS_PROSPECTS: () => '/ops/prospects',
  OPS_DEMO_PROSPECTS: () => '/ops/demo-prospects',
  OPS_AGENCIES: () => '/ops/agencies',
  OPS_AGENCY: (agencyId: string, tab: 'general' | 'users' = 'general') =>
    formatQueryParams(`/ops/agencies/${agencyId}`, { tab }),
  OPS_ENROLLMENT_MANAGEMENT: () => '/ops/enrollment-management',
  OPS_BULK_CARRIER_APPLICATION_ENROLLMENT_INFO: () =>
    `/ops/enrollment-management/bulk-application/enrollment-info`,
  OPS_BULK_CARRIER_APPLICATION_PAYMENT_METHOD: () =>
    `/ops/enrollment-management/bulk-application/payment-method`,
  OPS_BULK_CARRIER_APPLICATION_COMPLETE: () =>
    `/ops/enrollment-management/bulk-application/complete`,
  OPS_EMPLOYERS_OVERVIEW: (
    employerId: string,
    impersonatedEmployeeId?: string
  ) =>
    formatQueryParams(`/ops/employers/${employerId}/overview`, {
      impersonatedEmployeeId,
    }),
  OPS_EMPLOYERS_EMPLOYEES: (props: {
    employerId: string;
    impersonatedEmployeeId?: string;
    openWith?: string;
  }) =>
    formatQueryParams(`/ops/employers/${props.employerId}/employees`, {
      impersonatedEmployeeId: props.impersonatedEmployeeId,
      openWith: props.openWith,
    }),
  OPS_EMPLOYERS_ZORRO_PAY: (props: { employerId: string }) =>
    `/ops/employers/${props.employerId}/zorro-pay`,
  OPS_EMPLOYERS_PAYROLL: (props: { employerId: string; openWith?: string }) =>
    formatQueryParams(`/ops/employers/${props.employerId}/payroll`, {
      openWith: props.openWith,
    }),
  OPS_EMPLOYERS_COMPLIANCE: (props: { employerId: string }) =>
    `/ops/employers/${props.employerId}/compliance`,
  OPS_EMPLOYERS_DOCUMENTS: (props: { employerId: string; openWith?: string }) =>
    formatQueryParams(`/ops/employers/${props.employerId}/documents`, {
      openWith: props.openWith,
    }),
  OPS_EMPLOYERS_PLAN_FILTERS: (props: { employerId: string }) =>
    `/ops/employers/${props.employerId}/plan-filters`,
  OPS_EMPLOYERS_QUOTES: (props: { employerId: string }) =>
    `/ops/employers/${props.employerId}/quotes`,
  OPS_EMPLOYERS_NEW_QUOTE_WIZARD: (props: { employerId: string }) =>
    `/ops/employers/${props.employerId}/new-quote-wizard`,
  OPS_ADD_EMPLOYER_COMPANY_DETAILS: (employerId?: string) =>
    employerId
      ? `/ops/add-employer/${employerId}/company-details`
      : '/ops/add-employer',
  OPS_ADD_EMPLOYER_CONTACTS: (employerId: string) =>
    `/ops/add-employer/${employerId}/contacts`,
  OPS_ADD_EMPLOYER_COMPANY_SETUP: (employerId: string) =>
    `/ops/add-employer/${employerId}/company-setup`,
  OPS_ADD_EMPLOYER_ZORRO_SETUP: (employerId: string) =>
    `/ops/add-employer/${employerId}/zorro-setup`,
  OPS_ADD_EMPLOYER_PLAN_YEAR_SETUP: (employerId: string) =>
    `/ops/add-employer/${employerId}/plan-year-setup`,
  OPS_ADD_EMPLOYER_REVIEW: (employerId: string) =>
    `/ops/add-employer/${employerId}/review`,
  OPS_ADD_EMPLOYER_FINALIZE: (employerId: string) =>
    `/ops/add-employer/${employerId}/finalize`,

  OPS_COMPANY_SETUP_COMPANY_DETAILS: (employerId: string) =>
    `/ops/employers/${employerId}/company-setup/company-details`,
  OPS_COMPANY_SETUP_COMPANY_SETUP: (employerId: string) =>
    `/ops/employers/${employerId}/company-setup/company-setup`,
  OPS_COMPANY_SETUP_CONTACTS: (employerId: string) =>
    `/ops/employers/${employerId}/company-setup/contacts`,
  OPS_COMPANY_SETUP_ZORRO_SETUP: (employerId: string) =>
    `/ops/employers/${employerId}/company-setup/zorro-setup`,
  OPS_COMPANY_SETUP_PLAN_YEAR_SETUP: (employerId: string) =>
    `/ops/employers/${employerId}/company-setup/plan-year-setup`,
  OPS_COMPANY_SETUP_PAYROLL_SYNC: (employerId: string) =>
    `/ops/employers/${employerId}/company-setup/payroll-sync`,
  OPS_COMPANY_SETUP_PROSPECT: (employerId: string) =>
    `/ops/employers/${employerId}/company-setup/prospect`,

  OPS_EMPLOYEE_PAGE: ({
    employerId,
    employeeId,
    impersonatedEmployeeId,
  }: EmployeeProps) =>
    formatQueryParams(`/ops/employers/${employerId}/employees/${employeeId}`, {
      impersonatedEmployeeId,
    }),
  OPS_EMPLOYEE_PERSONAL_DETAILS: ({ employerId, employeeId }: EmployeeProps) =>
    `/ops/employers/${employerId}/employees/${employeeId}/personal-details`,
  OPS_EMPLOYEE_GENERAL_NOTES: ({ employerId, employeeId }: EmployeeProps) =>
    `/ops/employers/${employerId}/employees/${employeeId}/general-notes`,
  OPS_EMPLOYEE_NO_ENROLLMENTS: ({ employerId, employeeId }: EmployeeProps) =>
    `/ops/employers/${employerId}/employees/${employeeId}/no-enrollments`,
  OPS_EMPLOYEE_PAYMENT_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
  }: OnboardingPeriodProps) =>
    `/ops/employers/${employerId}/employees/${employeeId}/enrollments/${onboardingPeriodId}/payments`,

  OPS_EMPLOYEE_PLANS_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
    benefitId,
  }: EmployeePlansProps) =>
    formatQueryParams(
      `/ops/employers/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/plans`,
      { impersonatedEmployeeId, benefitId }
    ),
  OPS_EMPLOYEE_ACTIVITY_LOG_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
  }: OnboardingPeriodProps) =>
    formatQueryParams(
      `/ops/employers/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/activity-log`,
      { impersonatedEmployeeId }
    ),
  OPS_EMPLOYEE_FILES_TAB_V2: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
  }: OnboardingPeriodProps) =>
    formatQueryParams(
      `/ops/employers/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/files`,
      { impersonatedEmployeeId }
    ),
  OPS_EMPLOYEE_ZORRO_PAY_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
  }: OnboardingPeriodProps) =>
    formatQueryParams(
      `/ops/employers/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/zorro-pay`,
      { impersonatedEmployeeId }
    ),
  OPS_EMPLOYEE_MEMBERS_TAB: ({
    employerId,
    employeeId,
    onboardingPeriodId,
    impersonatedEmployeeId,
  }: OnboardingPeriodProps) =>
    formatQueryParams(
      `/ops/employers/${employerId}/employees/${employeeId}/v2/enrollments/${onboardingPeriodId}/members`,
      { impersonatedEmployeeId }
    ),

  OPS_CARRIER_APPLICATION_ENROLLMENT_INFO: ({
    employeeId,
    employerId,
    onboardingPeriodId,
  }: CarrierApplicationProps) =>
    `/ops/employers/${employerId}/employees/${employeeId}/application/${onboardingPeriodId}/enrollment-info`,
  OPS_CARRIER_APPLICATION_PAYMENT_METHOD: ({
    employeeId,
    employerId,
    onboardingPeriodId,
  }: CarrierApplicationProps) =>
    `/ops/employers/${employerId}/employees/${employeeId}/application/${onboardingPeriodId}/payment-method`,
  OPS_CARRIER_APPLICATION_COMPLETE: ({
    employeeId,
    employerId,
    onboardingPeriodId,
  }: CarrierApplicationProps) =>
    `/ops/employers/${employerId}/employees/${employeeId}/application/${onboardingPeriodId}/complete`,
  /** Session-authenticated launch page the Enrollment Copilot extension reads to
      learn which enrollment the operator is working. The ids stay on the Zorro
      origin — never on the carrier's URL. Contract:
      `.agents/agent-ops/enrollment-copilot/extension-integration-spec.md` → "Launch handoff". */
  ENROLLMENT_COPILOT_LAUNCH: ({
    benefitId,
    employeeId,
  }: EnrollmentCopilotLaunchProps) =>
    formatQueryParams(`/ops/enrollment-copilot/launch/${benefitId}`, {
      employeeId,
    }),
  // **********
};
